import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  endOfDay,
  endOfMonth,
  format,
  startOfMonth,
} from 'date-fns';
import { useMemo } from 'react';

import { isPrepaymentRequired } from '@/utils/bookingCartUtils';
import { getToday } from '@/utils/dateUtils';

import type {
  ReservationInterval,
  ReservationIntervalContext,
  ReservationIntervalShop,
} from './types';

const DEFAULT_ADVANCE_BOOK_DAYS = 30;
/**
 * Upper bound on the advance-booking window. Barbers/shops can't set above 365
 * in the backend, but guard here too so a misconfigured value can never open an
 * unbounded booking window.
 */
const MAX_ADVANCE_BOOK_DAYS = 365;
/**
 * Prepaid/deposit payment authorizations expire after 90 days, so the API
 * rejects bookings made further out than that. Cap the window here to match,
 * regardless of the barber/shop advance-booking setting.
 */
const MAX_PREPAID_ADVANCE_BOOK_DAYS = 90;
/** The calendar/schedule window never shrinks below this many months ahead. */
const MIN_MONTHS_AHEAD = 2;
/** Hard cap so a very long barber window can't fetch an unbounded date range. */
const MAX_MONTHS_AHEAD = 6;

/**
 * Computes the advance-booking interval `{ start, end }` for a reservation,
 * aligned with the WoW backend source of truth.
 *
 * The window is driven by the BARBER, not capped by the shop:
 * - Single specific barber  -> the barber's `advanceBookDays`.
 * - Any-barber selection    -> the shop's `advanceBookDays`.
 * - Multiple specific barbers in one cart -> the min across them (most restrictive wins).
 *
 * Boundary math is inclusive of today: `end = start + (N - 1)`, so N=30 means
 * the 30th day counting today is the last bookable day. N is clamped to
 * [1, 365] as a safety guard against a misconfigured advance-book value, or
 * [1, 90] when the shop/service requires prepayment or a deposit — payment
 * authorizations expire after 90 days, so the API rejects prepaid bookings
 * made further out than that.
 *
 * `start` is start-of-day "today" in the shop's timezone.
 */
export const useReservationInterval = (
  shop: ReservationIntervalShop | null | undefined,
  context?: ReservationIntervalContext,
): ReservationInterval => {
  const timezone = shop?.timezone ?? undefined;
  const shopAdvanceBookDays = shop?.advanceBookDays;
  const shopRequiresPrepayment = shop?.requiresPrepayment;
  const shopRequiresPartialDeposits = shop?.requiresPartialDeposits;
  // Only the cart items affect the window — depend on them (not the whole cart)
  // so unrelated cart mutations (tip, promo, payments) don't re-derive it.
  const items = context?.items;

  return useMemo(() => {
    // Anchor "today" to the shop's calendar day (as a local-midnight Date) so
    // the window matches the shop's day and aligns with the calendar grid.
    const start = getToday(timezone);

    const selectedItems = items ?? [];

    // One entry per selected barber: any-barber -> shop value, specific -> barber value.
    // With no cart (e.g. reschedule), fall back to a single any-barber selection
    // so the shop value (then the 30 default) is used.
    const values = (
      selectedItems.length > 0
        ? selectedItems.map((item) => {
            const isAnyBarber = item.bookedWithAnyBarber || !item.barber;
            return isAnyBarber
              ? shopAdvanceBookDays
              : item.barber?.advanceBookDays;
          })
        : [shopAdvanceBookDays]
    ).filter((value): value is number => Boolean(value));

    const effectiveValues =
      values.length > 0 ? values : [DEFAULT_ADVANCE_BOOK_DAYS];

    const requiresPrepayment = isPrepaymentRequired(
      {
        requiresPrepayment: shopRequiresPrepayment,
        requiresPartialDeposits: shopRequiresPartialDeposits,
      },
      items,
    );

    // Most restrictive barber wins; clamp to [1, MAX] so the window is never
    // negative and never exceeds the 365-day cap (90 when prepayment/deposit
    // is required, since payment authorizations expire after that).
    const advanceBookDays = Math.min(
      requiresPrepayment
        ? MAX_PREPAID_ADVANCE_BOOK_DAYS
        : MAX_ADVANCE_BOOK_DAYS,
      Math.max(1, Math.min(...effectiveValues)),
    );
    const daysToAdd = advanceBookDays - 1;
    const end = endOfDay(addDays(start, daysToAdd));

    // Month-aligned fetch/display window. The calendar renders whole months and
    // the schedule API fetches by month, so widen `end` out to its month — then
    // clamp to [MIN, MAX] months ahead so short windows still show a usable
    // range and long ones can't fetch an unbounded span.
    const rangeStart = startOfMonth(start);
    const monthsToEnd = differenceInCalendarMonths(end, rangeStart);
    const monthsAhead = Math.max(
      MIN_MONTHS_AHEAD,
      Math.min(MAX_MONTHS_AHEAD, monthsToEnd),
    );
    const rangeEnd = endOfMonth(addMonths(rangeStart, monthsAhead));

    return {
      start,
      end,
      startStr: format(start, 'yyyy-MM-dd'),
      endStr: format(end, 'yyyy-MM-dd'),
      rangeStart,
      rangeEnd,
      monthsAhead,
    };
  }, [
    timezone,
    shopAdvanceBookDays,
    shopRequiresPrepayment,
    shopRequiresPartialDeposits,
    items,
  ]);
};
